import { memo } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { Form, FormExternalConditionData } from '@components/form';
import { FormLabel } from '@components/form-label';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconTrash } from '@components/icons/trash';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';

import { useDynamicFieldsContext } from './context';
import { DateInput } from './date-input';
import { FileInput, FileMultiInput } from './file-input';
import { List } from './list';
import { useTranslatedFieldText } from './locale';
import { PhoneInput } from './phone-input';
import { RadioInput } from './radio-input';
import { SelectInput, SelectMultiInput } from './select-input';
import { Heading, Label, Paragraph, SubHeading } from './text-components';
import { TextInput } from './text-input';
import {
  ConditionalField,
  Field,
  FormGroupField,
  RepeatingField,
  RepeatingFieldEntry,
} from './types';

type FieldProps<T = Field> = {
  field: T;
  externalData?: FormExternalConditionData;
};

export const RenderDynamicField = memo<FieldProps>(({ externalData, field }) => {
  switch (field.type) {
    case 'h2':
      return <Heading field={field} />;
    case 'h3':
      return <SubHeading field={field} />;
    case 'list':
      return <List field={field} />;
    case 'conditional':
      return <Conditional externalData={externalData} field={field} />;
    case 'label':
      return <Label field={field} />;
    case 'p':
      return <Paragraph field={field} />;
    case 'file':
      return <FileInput field={field} />;
    case 'file-multi':
      return <FileMultiInput field={field} />;
    case 'sep':
      return <Separator margin="0.5rem 0" />;
    case 'formField':
      return <TextInput field={field} />;
    case 'dropdown':
      return <SelectInput field={field} />;
    case 'dateField':
      return <DateInput field={field} />;
    case 'formGroup':
      return <FormGroup field={field} externalData={externalData} />;
    case 'radioGroup':
      return <RadioInput field={field} />;
    case 'repeating':
      return <Repeating field={field} externalData={externalData} />;
    case 'selectMulti':
      return <SelectMultiInput field={field} />;
    case 'phone':
      return <PhoneInput field={field} />;
    default:
      return null;
  }
});

const Conditional = ({ field, externalData }: FieldProps<ConditionalField>) => {
  return (
    <Form.Conditional condition={field.condition} data={externalData}>
      <Stack space={4}>
        {field.fields.map((childField) => (
          <RenderDynamicField key={childField.id} field={childField} externalData={externalData} />
        ))}
      </Stack>
    </Form.Conditional>
  );
};

const Repeating = memo<FieldProps<RepeatingField>>(({ field, externalData }) => {
  const removeText = useTranslatedFieldText(field.removeText);
  const addText = useTranslatedFieldText(field.addText);
  const labelText = useTranslatedFieldText(field.label);
  const descriptionText = useTranslatedFieldText(field.description);
  const { addRepeatingFieldEntry } = useDynamicFieldsContext();

  return (
    <Stack space={4} style={{ overflow: 'hidden' }}>
      <FormLabel text={labelText} />
      {descriptionText && <Text text={descriptionText} />}

      <AnimatePresence initial={false}>
        {(field.entries ?? []).map((item) => (
          <RepeatingFieldChild
            key={item.id}
            externalConditionData={externalData}
            disableRemove={(field.entries ?? []).length < 2}
            removeText={removeText}
            item={item}
          />
        ))}
      </AnimatePresence>
      <Stack space={0} orientation="horizontal" justify="flex-end">
        <Button
          variant="secondary"
          iconLeft={<IconAddPlus />}
          text={addText}
          onClick={() => addRepeatingFieldEntry(field.id)}
        />
      </Stack>
    </Stack>
  );
});

type RepeatingFieldChildProps = {
  item: RepeatingFieldEntry;
  externalConditionData?: FormExternalConditionData;
  removeText: string;
  disableRemove: boolean;
};

const RepeatingFieldChild = ({
  item,
  removeText,
  disableRemove,
  externalConditionData: externalData,
}: RepeatingFieldChildProps) => {
  const { removeRepeatingFieldEntry } = useDynamicFieldsContext();
  return (
    <motion.div
      animate={{ opacity: 1, scale: 1 }}
      initial={{ opacity: 0, scale: 0.9 }}
      exit={{
        opacity: 0,
        scale: 0,
        height: 0,
        overflow: 'hidden',
        transition: { duration: 0.25 },
      }}
    >
      <Card variant="transparent" space={6}>
        <Stack space={6}>
          <Stack space={4}>
            {item.fields.map((field) => (
              <RenderDynamicField key={field.id} field={field} externalData={externalData} />
            ))}
          </Stack>
          <Stack orientation="horizontal" space={2} justify="flex-end">
            <Button
              isDisabled={disableRemove}
              variant="secondary"
              iconLeft={<IconTrash />}
              text={removeText}
              onClick={() => removeRepeatingFieldEntry(item.id)}
            />
          </Stack>
        </Stack>
      </Card>
    </motion.div>
  );
};

const FormGroup = memo<FieldProps<FormGroupField>>(({ field, externalData }) => {
  return (
    <FormGroupContainer>
      {field.fields.map((childField) => (
        <RenderDynamicField key={childField.id} field={childField} externalData={externalData} />
      ))}
    </FormGroupContainer>
  );
});

const FormGroupContainer = styled(Stack).attrs({
  space: 4,
  orientation: 'horizontal',
})({
  flexDirection: 'column',
  '> *': {
    flex: '1 1',
  },
  '@media(min-width: 768px)': {
    flexDirection: 'row',
  },
});
